import { Component, EventEmitter, Input, Output } from '@angular/core';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faCircle,
  faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import { SVGIcon } from '@progress/kendo-svg-icons';
import { FaIconLibrary } from '@fortawesome/angular-fontawesome';
import { BehaviorSubject, take, takeUntil } from 'rxjs';
import { DialogCloseResult } from '@progress/kendo-angular-dialog';
import { AbstractComponent } from 'src/app/shared/components/abstract-component';
import { MarkingWarnService } from '../../services/marking-warn.service';
import {
  NotificationAction,
  NotificationReadAllBit,
  NotificationTriggerPoint,
  NotificationType,
} from 'src/app/shared/enums/notification';
import { AllNotificationsResponse } from '../../interface/get-all-notifications-response';
import { RoutesConstantService } from 'src/app/services/routes-constant.service';
import { Router } from '@angular/router';
import { ASSESSMENT_INFO } from 'src/app/modules/evaluations/assessment/constants/assessment-routes';
import { find, get } from 'lodash';
import { DashboardService } from '../../services/dashboard.service';
import { MarkPayload } from '../../interface/mark-payload';
import { NotificationService } from '@progress/kendo-angular-notification';
import { MeetingType } from 'src/app/shared/enums/meeting-type';
import { MarkReadNotificationService } from '../../services/notification.service';
import _ from 'lodash';
import { MeetingSettingType } from 'src/app/shared/enums/meeting-setting-type';
import { dialogTimer } from 'src/app/shared/constants/dialog-specs';

@Component({
  selector: 'app-notification-content',
  templateUrl: './notification-content.component.html',
  styleUrls: ['./notification-content.component.scss'],
  standalone: false,
})
export class NotificationContentComponent extends AbstractComponent {
  notificationType: typeof NotificationType = NotificationType;
  notificationAction: typeof NotificationAction = NotificationAction;
  circle: SVGIcon = this.customFaIcon(faCircle as IconDefinition);
  triangle: SVGIcon = this.customFaIcon(
    faTriangleExclamation as IconDefinition
  );
  @Input() content: AllNotificationsResponse[] = [];
  @Input() isAllNotificationView: boolean = false;
  dataNycId: string;
  meetingRoster = [
    {
      label: 'SCHOOL SOCIAL WORKERS (MANDATED SERVICES)',
      title: 'SCHOOL SOCIAL WORKERS (MANDATED SERVICES)',
      isRequired: true,
      isSticky: true,
      members: [],
    },
  ];
  meetingTypeENUM = MeetingType;
  referralSk: string;
  iepMeetingActionText: string = 'Schedule IEP Meeting';
  showScheduleMeetingDialog$: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);
  caseStage: string;

  constructor(
    library: FaIconLibrary,
    private markingWarnService: MarkingWarnService,
    private routesConstantService: RoutesConstantService,
    private router: Router,
    private dashboardService: DashboardService,
    private notificationService: NotificationService,
    private markReadNotificationService: MarkReadNotificationService
  ) {
    super();
    library.addIcons(faTriangleExclamation);
  }

  customFaIcon(icon: IconDefinition): SVGIcon {
    return {
      name: icon.iconName,
      // eslint-disable-next-line @typescript-eslint/no-magic-numbers
      content: `<path d="${icon.icon[4]}" />`,
      // eslint-disable-next-line @typescript-eslint/no-magic-numbers
      viewBox: `0 0 ${icon.icon[0]} ${icon.icon[1]}`,
    };
  }

  // Mark the individual notification as read.
  markingNotification(payload: MarkPayload[], isViewAction? : boolean, notificationData?: AllNotificationsResponse): void {
    this.dashboardService
      .markNotification(payload)
      .pipe(takeUntil(this.componentDestroyed))
      .subscribe({
        next: () => {
          if(isViewAction)  {
            this.viewNotification(notificationData);
          }else {
          this.notificationService.show({
            content: 'Marking notifications successful.',
            cssClass: 'notification-success',
            hideAfter: dialogTimer,
            position: { horizontal: 'center', vertical: 'top' },
            animation: { type: 'slide', duration: 60 },
            type: { style: 'success', icon: true },
          });
          }
          //Refresh the notification list
          this.markReadNotificationService.notifyNotificationMarked(isViewAction);
          // this.getNotificationSummary.emit();
        },
        error: () => {
          this.notificationService.show({
            content: 'Marking notifications failed. Please try again later.',
            cssClass: 'notification-error',
            position: { horizontal: 'center', vertical: 'top' },
            animation: { type: 'slide', duration: 60 },
            type: { style: 'error', icon: true },
            closable: true,
          });
        },
      });
  }

public mark(notificationData: AllNotificationsResponse, isViewAction?: boolean): void {
  
  if(isViewAction && notificationData.notificationStatus === NotificationAction.Read) { // If already read, just allow user to view
    this.viewNotification(notificationData);
    return;
  }
  const payload: MarkPayload[] = [
    {
      notificationSk: notificationData.notificationSk,
      studentSk: notificationData.studentSK,
      notificationTypeId: notificationData.notificationTypeId,
      notificationStatus: NotificationAction.Read,
      markAllRead: NotificationReadAllBit.None,
      authUserRoleLocationSk: notificationData.authUserRoleLocationSk
    },
  ];

        // Warning suppressed, directly mark notification
        this.markingNotification(payload, isViewAction, notificationData);
}


  override afterOnDestroy(): void {}

  viewNotification(dataItem: AllNotificationsResponse): void {
    let route: string = this.evaluationDataRoute(dataItem?.evaluationType);
    switch (dataItem.notificationTypeId) {
      case NotificationTriggerPoint.AdditionalAssessmentApprovalNeeded:
        // Handle route for AdditionalAssessmentApprovalNeeded notification
        this.router.navigate([
          this.routesConstantService.routes.evaluations +
            dataItem.referralSk +
            '/' +
            dataItem?.nycID +
            '/' +
            dataItem.caseActionSk +
            '/edit/pwn/request-additional-assessments',
        ]);
        break;
      case NotificationTriggerPoint.AdditionalAssessmentApprovalNeededDuplicate:
        // Handle route for AdditionalAssessmentApprovalNeededDuplicate notification
        this.router.navigate([
          this.routesConstantService.routes.evaluations +
            dataItem.referralSk +
            '/' +
            dataItem?.nycID +
            '/' +
            dataItem.caseActionSk +
            '/view/pwn/request-additional-assessments',
        ]);
        break;
      case NotificationTriggerPoint.AssessmentReportSubmittedByDOEEvaluator:  
      case NotificationTriggerPoint.AssessmentReportSubmittedByContractedEvaluator:
          if (route) {
            this.router.navigate([
              this.routesConstantService.routes.evaluations +
                dataItem?.referralSk +
                '/' +
                dataItem?.evaluationSk +
                '/' +
                dataItem?.nycID +
                '/reviewassessment/' +
                route,
            ]);
        }
        break;
      case NotificationTriggerPoint.IEPMeetingNotScheduledWithin30DaysAfterConsent:
        // Handle route for IEPMeetingNotScheduledWithin30DaysAfterConsent notification
        //On click Route to IEP Document Meeting Schedule
        this.dataNycId = dataItem.nycID;
        this.referralSk = dataItem.referralSk;
        this.caseStage = dataItem.caseStage ?? MeetingSettingType.Initial?.toString(); // If N/A make it to 1 default
        this.openScheduleMeetingDialog();
        break;
      case NotificationTriggerPoint.NoFADWithin30DaysOfSEITAssignment:
      case NotificationTriggerPoint.ChildrenAwaitingPlacementSpecialClassSCIS:
        // Handle route for NoFADWithin30DaysOfSEITAssignment notification
        //Student Profile - Service Details tab
        this.router.navigate([/student/ + dataItem?.nycID]);
        break;
      case NotificationTriggerPoint.ArrangeProgramAndServicesFirstCome:
        // Handle route for ArrangeProgramAndServicesFirstCome notification
        //No route
        break;
      case NotificationTriggerPoint.STAC5Submitted:
        // Handle route for STAC5Submitted notification
        // Route to dashboard, filtered by student name ==> No  route
        this.dashboardService.setNotificationReloadData(dataItem.nycID);
        if(!this.router.url.includes('dashboard')) {
          this.router.navigate(['/dashboard']);
        } else
          this.dashboardService.getReloadDashboardGrid(true);
        break;
      case NotificationTriggerPoint.StudentAddedToWaitlist:
        // Handle route for StudentAddedToWaitlist notification
        this.router.navigate(['/wait-list/MDE/' + dataItem?.caseActionSk]);
        break;
      case NotificationTriggerPoint.AssessmentAssignedToEvaluator:
        // Handle route for AssessmentAssignedToEvaluator notification
          if (route) {
            this.router.navigate([
              this.routesConstantService.routes.evaluations +
                dataItem?.referralSk +
                '/' +
                dataItem?.evaluationSk +
                '/' +
                dataItem?.nycID +
                '/edit/' +
                route,
            ]);
          }
        break;
      case NotificationTriggerPoint.AssessmentAssignedToSecondaryAgency:
        // Handle route for AssessmentAssignedToSecondaryAgency notification
        //On Click: Route the user to assignment screen. // assessment planning screen
        this.router.navigate([
          this.routesConstantService.routes.evaluations +
            dataItem.referralSk +
            '/' +
            dataItem.nycID +
            '/edit/assessment-planning',
        ]);
        break;

      default:
        // Handle unknown notification type
        this.router.navigate([/student/ + dataItem?.nycID]);
        break;
    }
  }

  evaluationDataRoute(evaluationType: number): string {
    return get(find(ASSESSMENT_INFO, { evaluationID: evaluationType }), 'route');
  }

  showViewButton(dataItem: AllNotificationsResponse): boolean {
    return dataItem.type === NotificationType.ActionRequired && dataItem.actions == NotificationAction.Created;
  }

  isViewButtonDisabled(dataItem: AllNotificationsResponse): boolean {
    return (
      dataItem.actions !== 5 &&
      dataItem.actions !== NotificationAction.Created
    );
  }

  openScheduleMeetingDialog(): void {
    this.showScheduleMeetingDialog$.next(true);
  }

  closeScheduleMeetingDialog(): void {
    this.showScheduleMeetingDialog$.next(false);
  }
}
